<script setup lang="ts">
import { brandRef } from "../brand-state";
/**
 * My sign-offs — what this person signs or confirms, and nothing else.
 *
 * The admin's to-do list names who has to act; this is where they act,
 * without admin rights. Two kinds of item, each done by the person
 * themselves with their own sign-in (ADR 0062 amendment):
 *
 *   - **Agreements to sign** — a data access agreement where they hold one
 *     of the signing roles. They read it here, and signing records who,
 *     when, which version and the hash of the exact text.
 *   - **Access to confirm** — as a source's data owner, that everyone in an
 *     assistant's group already has full access in the source system.
 *   - **Team requests** (ADR 0065) — as a team's owner, whether someone
 *     joins or leaves. Approving makes DOIT AI write the team's Entra group.
 *     Only for an `entra_write` team: a team that follows its Entra group
 *     read-only (ADR 0074, the default) takes no requests — its membership
 *     is changed in Entra by the group's owner — so none appear here.
 *   - **Assistants to accept** (ADR 0066) — as an AI Board member, that an
 *     assistant may be used, once its data owners have confirmed and its
 *     access tests pass. Data owners also see those tests, and can add one
 *     for their own source.
 *
 * A signature given here is stronger evidence than one an admin records
 * on someone's behalf: the platform authenticated who signed.
 */
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type AgreementResponse,
  type MyAcceptItem,
  type MyAgreementItem,
  type MyChecks,
  type MyConfirmItem,
  type MySignOffs,
  type TeamMembershipRequest,
  acceptMyAssistant,
  addMyAccessTest,
  confirmMyAccess,
  decideTeamRequest,
  getMyAgreement,
  getMySignOffs,
  signMyAgreement,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import AgreementViewer from "./AgreementViewer.vue";
import ReadinessSteps from "./ReadinessSteps.vue";
import SidePanel from "./SidePanel.vue";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();
const emit = defineEmits<{
  (e: "exit"): void;
  (e: "changed", count: number): void;
}>();

const toast = useToast();
const { confirm } = useConfirm();

const data = ref<MySignOffs | null>(null);
const loading = ref(false);
const busy = ref("");

function fail(e: unknown, title: string): void {
  toast.error(title, e instanceof AdminError ? e.message : String(e));
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    data.value = await getMySignOffs(props.devUser);
    emit("changed", data.value.count);
  } catch (e) {
    fail(e, "Could not load your sign-offs");
  } finally {
    loading.value = false;
  }
}
onMounted(load);

// ── Team requests (ADR 0065) ──────────────────────────────────────────────

const notes = ref<Record<number, string>>({});
async function decide(
  r: TeamMembershipRequest,
  approve: boolean,
): Promise<void> {
  const who = `${r.action === "add" ? "add" : "remove"} ${r.email}`;
  if (!approve) {
    const ok = await confirm({
      title: `Reject the request to ${who}?`,
      message: `${r.team_name}: nothing changes in Entra. The requester sees your note.`,
      confirmLabel: "Reject",
      danger: true,
    });
    if (!ok) return;
  }
  busy.value = `team:${r.id}`;
  try {
    const out = await decideTeamRequest(
      r.id,
      approve,
      notes.value[r.id] ?? "",
      props.devUser,
    );
    if (out.status === "applied") {
      toast.success(
        `${r.team_name}: ${who} — done`,
        r.action === "add"
          ? "They are in the Entra group; access follows at their next sign-in."
          : "Removed from the Entra group; access ends at their next sign-in.",
      );
    } else if (out.status === "failed") {
      toast.error(
        `${r.team_name}: approved, but not written to Entra`,
        out.error,
      );
    } else {
      toast.success(`${r.team_name}: rejected`);
    }
    await load();
  } catch (e) {
    fail(e, "Could not record the decision");
  } finally {
    busy.value = "";
  }
}

// ── Reading ───────────────────────────────────────────────────────────────

const agreement = ref<AgreementResponse | null>(null);
const readingKey = ref("");
const readerOpen = computed({
  get: () => agreement.value !== null,
  set: (v: boolean) => {
    if (!v) agreement.value = null;
  },
});
/** An item may only be signed once its text has been opened here. */
const opened = ref<Set<string>>(new Set());

async function read(item: MyAgreementItem): Promise<void> {
  readingKey.value = item.source_key;
  try {
    agreement.value = await getMyAgreement(item.source_key, props.devUser);
    opened.value = new Set([...opened.value, item.source_key]);
  } catch (e) {
    fail(e, "Could not open the agreement");
  } finally {
    readingKey.value = "";
  }
}

// ── Signing ───────────────────────────────────────────────────────────────

async function sign(item: MyAgreementItem): Promise<void> {
  const open = item.decisions.filter((d) => d.kind === "needs_review").length;
  const ok = await confirm({
    title: `Sign ${item.agreement_id} as ${item.role}?`,
    message:
      `You confirm that you have read ${item.agreement_id} version ${item.version} (${item.source}) ` +
      `and accept it as written` +
      (item.assumed
        ? `, including ${item.assumed} assumptions marked amber`
        : "") +
      (open
        ? `. ${open} ${open === 1 ? "decision is" : "decisions are"} still open for someone else; signing accepts the text as it stands`
        : "") +
      `. Your sign-in, the time and the exact text are recorded.`,
    confirmLabel: "Sign",
  });
  if (!ok) return;
  busy.value = `sign:${item.source_key}`;
  try {
    data.value = await signMyAgreement(
      item.source_key,
      item.role,
      props.devUser,
    );
    emit("changed", data.value.count);
    toast.success(`Signed ${item.agreement_id}`);
  } catch (e) {
    fail(e, "Could not sign");
  } finally {
    busy.value = "";
  }
}

// ── Confirming access ─────────────────────────────────────────────────────

async function confirmAccess(item: MyConfirmItem): Promise<void> {
  const what = item.parts?.length ? item.parts.join(", ") : item.source;
  const ok = await confirm({
    title: `Confirm access to ${item.assistant}?`,
    message: `You confirm that everyone in ${item.groups.join(", ")} may see ${what} from ${item.source}, and already has that access there. This covers ${item.source} only. It stops counting if who can open the assistant changes.`,
    confirmLabel: "Confirm",
  });
  if (!ok) return;
  busy.value = `confirm:${item.slug}:${item.source_key}`;
  try {
    data.value = await confirmMyAccess(
      item.slug,
      "",
      props.devUser,
      item.source_key,
    );
    emit("changed", data.value.count);
    toast.success(`Confirmed for ${item.assistant}`);
  } catch (e) {
    fail(e, "Could not confirm");
  } finally {
    busy.value = "";
  }
}

// ── Accepting for use (AI Board, ADR 0066) ────────────────────────────────

async function accept(a: MyAcceptItem): Promise<void> {
  const ok = await confirm({
    title: `Accept ${a.assistant} for use?`,
    message:
      `You accept, for the AI Board, that ${a.assistant} may be used by ` +
      `${a.groups.join(", ") || "everyone who can sign in"} as it runs now: its data ` +
      `owners have confirmed and its access tests pass. It lasts a year, or ` +
      `until the assistant changes. Your sign-in and the time are recorded.`,
    confirmLabel: "Accept",
  });
  if (!ok) return;
  busy.value = `accept:${a.slug}`;
  try {
    data.value = await acceptMyAssistant(a.slug, "", props.devUser);
    emit("changed", data.value.count);
    toast.success(`${a.assistant} accepted for use`);
  } catch (e) {
    fail(e, "Could not accept");
  } finally {
    busy.value = "";
  }
}

const VERDICT = { pass: "✓", fail: "✗", inconclusive: "?" } as const;

function checkRows(c: MyChecks) {
  return c.results.map((r) => ({
    key: `${r.test_id}|${r.persona}`,
    question: r.question,
    who: r.persona_label,
    expect: r.expect === "sees" ? "should find it" : "should be kept out",
    verdict: r.verdict,
  }));
}

// ── A data owner's own check ──────────────────────────────────────────────

const checkDraft = ref<Record<string, { question: string; look_for: string }>>(
  {},
);
function draftKey(c: MyConfirmItem): string {
  return `${c.slug}:${c.source_key}`;
}
function openCheck(c: MyConfirmItem): void {
  checkDraft.value = {
    ...checkDraft.value,
    [draftKey(c)]: { question: "", look_for: "" },
  };
}
async function addCheck(c: MyConfirmItem): Promise<void> {
  const d = checkDraft.value[draftKey(c)];
  if (!d) return;
  busy.value = `check:${draftKey(c)}`;
  try {
    data.value = await addMyAccessTest(
      c.slug,
      {
        question: d.question.trim(),
        look_for: d.look_for.trim(),
        source_key: c.source_key,
        personas: [
          ...(c.audience ?? []).map((p) => ({
            as: p,
            expect: "sees" as const,
          })),
          { as: "everyone", expect: "blocked" as const },
        ],
      },
      props.devUser,
    );
    const { [draftKey(c)]: _done, ...rest } = checkDraft.value;
    checkDraft.value = rest;
    toast.success("Check added and run");
  } catch (e) {
    fail(e, "Could not add the check");
  } finally {
    busy.value = "";
  }
}

const nothing = computed(
  () =>
    !!data.value &&
    !data.value.to_sign.length &&
    !data.value.to_confirm.length &&
    !data.value.signed.length &&
    !data.value.confirmed.length &&
    !(data.value.to_accept ?? []).length &&
    !(data.value.accepted ?? []).length &&
    !(data.value.team_requests ?? []).length,
);
const acceptable = computed(
  () => (data.value?.to_accept ?? []).filter((a) => a.readiness.can_accept)
    .length,
);
</script>

<template>
  <div class="mine">
    <header class="mine__head">
      <div>
        <h1>My sign-offs</h1>
        <p v-if="data?.roles.length" class="muted">
          You are named as: {{ data.roles.join(" · ") }}
        </p>
      </div>
      <button class="link" @click="emit('exit')">← back</button>
    </header>

    <SkeletonRows v-if="loading && !data" :rows="4" />

    <template v-else-if="data">
      <p v-if="data.review_round" class="round">
        <strong>{{ data.review_round.title }}</strong> is open<template
          v-if="data.review_round.due_on"
        >
          until {{ data.review_round.due_on }}</template
        >. Everything confirmed or accepted before
        {{ data.review_round.opened_at.slice(0, 10) }} is due again — it is
        listed below.
      </p>
      <p v-if="nothing" class="muted">
        Nothing here is waiting for you. When you are named as a signer of a
        data access agreement, or as the owner of a data source, what you need
        to sign or confirm will show up here.
      </p>
      <p
        v-else-if="
          !data.to_sign.length &&
          !data.to_confirm.length &&
          !acceptable &&
          !(data.team_requests ?? []).length
        "
        class="clear"
      >
        You are up to date — nothing waiting for you.
      </p>

      <!-- Accept for use (AI Board) -->
      <section v-if="(data.to_accept ?? []).length" class="block">
        <h2>Assistants to accept · {{ acceptable }} ready</h2>
        <p class="muted small">
          As the AI Board, you accept that an assistant may be used. It is ready
          when its data owners have confirmed who may see their data and its
          access tests show it: the right people find the answer, the rest do
          not.
        </p>
        <article
          v-for="a in data.to_accept"
          :key="a.slug"
          class="item item--col"
        >
          <div class="item__row">
            <div class="item__main">
              <div class="item__title">
                {{ a.assistant }}
                <span v-if="a.maturity === 'pilot'" class="muted">· pilot</span>
              </div>
              <div class="small">
                Used by
                <strong>{{
                  a.groups.join(", ") || "everyone who can sign in"
                }}</strong>
                <template v-if="a.intended_use"> — {{ a.intended_use }}</template>
              </div>
              <div v-if="a.accepted_by" class="small warn-text">
                Accepted before by {{ a.accepted_by }} on
                {{ (a.accepted_at ?? "").slice(0, 10) }} — due again.
              </div>
            </div>
            <div class="item__actions">
              <button
                class="btn btn--primary"
                :disabled="!a.readiness.can_accept || busy === `accept:${a.slug}`"
                :title="
                  a.readiness.can_accept ? '' : 'Waiting for steps 1 and 2'
                "
                @click="accept(a)"
              >
                {{ busy === `accept:${a.slug}` ? "Accepting…" : "Accept for use" }}
              </button>
            </div>
          </div>
          <ReadinessSteps :readiness="a.readiness" compact />
          <details v-if="a.checks.results.length" class="small checks">
            <summary>
              The access tests — {{ a.checks.summary.detail }}
            </summary>
            <table>
              <tbody>
                <tr v-for="r in checkRows(a.checks)" :key="r.key">
                  <td>“{{ r.question }}”</td>
                  <td>{{ r.who }}</td>
                  <td class="muted">{{ r.expect }}</td>
                  <td :class="`v v--${r.verdict}`">{{ VERDICT[r.verdict] }}</td>
                </tr>
              </tbody>
            </table>
          </details>
        </article>
      </section>

      <!-- Agreements -->
      <section v-if="data.to_sign.length" class="block">
        <h2>Agreements to sign · {{ data.to_sign.length }}</h2>
        <p class="muted small">
          Each lets {{ brandRef.productName }} read one system. Read it, then sign. If something in
          it is wrong, don't sign — tell the person who sent it to you.
        </p>
        <article
          v-for="i in data.to_sign"
          :key="`${i.source_key}:${i.role}`"
          class="item"
        >
          <div class="item__main">
            <div class="item__title">
              {{ i.source }}
              <span class="muted">· {{ i.system }}</span>
            </div>
            <div class="small">
              {{ i.agreement_id }} version {{ i.version }} — you sign as
              <strong>{{ i.role }}</strong>
            </div>
            <div v-if="i.previous" class="small warn-text">
              You signed version {{ i.previous.agreement_version }}; this
              version has changed and needs your signature again.
            </div>
            <details v-if="i.decisions.length" class="small decisions">
              <summary>
                {{ i.decisions.length }} open
                {{ i.decisions.length === 1 ? "point" : "points" }} in the text
                — some are for others to settle
              </summary>
              <ul>
                <li
                  v-for="(d, n) in i.decisions"
                  :key="n"
                  :class="d.kind === 'needs_data' ? 'gap-text' : ''"
                >
                  {{ d.text }}
                </li>
              </ul>
            </details>
          </div>
          <div class="item__actions">
            <button
              class="btn"
              :disabled="readingKey === i.source_key"
              @click="read(i)"
            >
              {{ readingKey === i.source_key ? "Opening…" : "Read" }}
            </button>
            <button
              class="btn btn--primary"
              :disabled="
                i.blocked ||
                !opened.has(i.source_key) ||
                busy === `sign:${i.source_key}`
              "
              :title="
                i.blocked
                  ? 'Values are missing in the agreement — it cannot be signed yet'
                  : !opened.has(i.source_key)
                    ? 'Read it first'
                    : ''
              "
              @click="sign(i)"
            >
              {{ busy === `sign:${i.source_key}` ? "Signing…" : "Sign" }}
            </button>
          </div>
        </article>
      </section>

      <!-- Access -->
      <section v-if="data.to_confirm.length" class="block">
        <h2>Access to confirm · {{ data.to_confirm.length }}</h2>
        <p class="muted small">
          These assistants let a whole group see all of your source's data. As
          its owner, confirm that everyone in the group already has that access
          in the source system itself.
        </p>
        <article
          v-for="c in data.to_confirm"
          :key="`${c.slug}:${c.source_key}`"
          class="item"
        >
          <div class="item__main">
            <div class="item__title">{{ c.assistant }}</div>
            <div class="small">
              <strong>{{ c.groups.join(", ") }}</strong> sees
              {{ c.parts?.length ? c.parts.join(", ") : "all of it" }} from
              {{ c.source }}
            </div>
            <div v-if="c.stale" class="small warn-text">
              Confirmed before by {{ c.confirmed_by }}, but the group or the
              sources have changed since.
            </div>
            <div v-else-if="c.due_in_round" class="small warn-text">
              You confirmed this on {{ c.confirmed_at.slice(0, 10) }} — due
              again in this review round.
            </div>
            <details v-if="c.checks" class="small checks">
              <summary>
                Access tests —
                {{ c.checks.results.length ? c.checks.summary.detail : "none yet" }}
              </summary>
              <table v-if="c.checks.results.length">
                <tbody>
                  <tr v-for="r in checkRows(c.checks)" :key="r.key">
                    <td>“{{ r.question }}”</td>
                    <td>{{ r.who }}</td>
                    <td class="muted">{{ r.expect }}</td>
                    <td :class="`v v--${r.verdict}`">{{ VERDICT[r.verdict] }}</td>
                  </tr>
                </tbody>
              </table>
              <form
                v-if="checkDraft[draftKey(c)]"
                class="check-form"
                @submit.prevent="addCheck(c)"
              >
                <input
                  v-model="checkDraft[draftKey(c)].question"
                  placeholder="A question only the audience should get answered"
                  maxlength="500"
                  required
                />
                <input
                  v-model="checkDraft[draftKey(c)].look_for"
                  placeholder="Text in the document's title or path"
                  maxlength="300"
                  required
                />
                <span class="muted">
                  {{ c.groups.join(", ") }} should find it; everyone else should
                  be kept out.
                </span>
                <button
                  class="btn btn--primary"
                  :disabled="busy === `check:${draftKey(c)}`"
                >
                  {{ busy === `check:${draftKey(c)}` ? "Adding…" : "Add and run" }}
                </button>
              </form>
              <button v-else type="button" class="link" @click="openCheck(c)">
                + Add a check from your data
              </button>
            </details>
          </div>
          <div class="item__actions">
            <button
              class="btn btn--primary"
              :disabled="busy === `confirm:${c.slug}:${c.source_key}`"
              @click="confirmAccess(c)"
            >
              {{
                busy === `confirm:${c.slug}:${c.source_key}`
                  ? "Confirming…"
                  : "Confirm"
              }}
            </button>
          </div>
        </article>
      </section>

      <!-- Team requests -->
      <section v-if="(data.team_requests ?? []).length" class="block">
        <h2>Team requests · {{ data.team_requests.length }}</h2>
        <p class="muted small">
          You own these teams, and {{ brandRef.productName }} may change their Entra groups.
          Approving adds or removes the person in the group — that is what
          gives or ends their access.
        </p>
        <article v-for="r in data.team_requests" :key="r.id" class="item">
          <div class="item__main">
            <div class="item__title">
              {{ r.action === "add" ? "Add" : "Remove" }} {{ r.email }}
              <span class="muted">· {{ r.team_name }}</span>
            </div>
            <div class="small muted">
              Asked by {{ r.requested_by }} on {{ r.requested_at.slice(0, 10)
              }}<template v-if="r.reason"> — “{{ r.reason }}”</template>
            </div>
            <div v-if="r.status === 'failed'" class="small warn-text">
              Approved, but not written to Entra: {{ r.error }}
            </div>
            <input
              v-model="notes[r.id]"
              class="note"
              placeholder="Note (optional)"
              maxlength="1000"
              :aria-label="`Note for ${r.email}`"
            />
          </div>
          <div class="item__actions">
            <button
              class="btn btn--primary"
              :disabled="busy === `team:${r.id}`"
              @click="decide(r, true)"
            >
              {{ r.status === "failed" ? "Retry" : "Approve" }}
            </button>
            <button
              v-if="r.status === 'pending'"
              class="btn"
              :disabled="busy === `team:${r.id}`"
              @click="decide(r, false)"
            >
              Reject
            </button>
          </div>
        </article>
      </section>

      <!-- Done -->
      <section
        v-if="
          data.signed.length ||
          data.confirmed.length ||
          (data.accepted ?? []).length
        "
        class="block done"
      >
        <h2>Done</h2>
        <ul class="small">
          <li v-for="i in data.signed" :key="`s:${i.source_key}:${i.role}`">
            <StatusChip tone="ok">Signed</StatusChip>
            {{ i.agreement_id }} v{{ i.version }} ({{ i.source }}) as
            {{ i.role }} on {{ i.signed_on }}
            <span v-if="i.recorded?.method === 'recorded'" class="muted">
              — recorded by {{ i.recorded.recorded_by_email }}</span
            >
          </li>
          <li v-for="a in data.accepted ?? []" :key="`a:${a.slug}`">
            <StatusChip tone="ok">Accepted</StatusChip>
            {{ a.assistant }} — by {{ a.accepted_by }} on
            {{ (a.accepted_at ?? "").slice(0, 10) }}, until {{ a.valid_until }}
          </li>
          <li v-for="c in data.confirmed" :key="`c:${c.slug}:${c.source_key}`">
            <StatusChip tone="ok">Confirmed</StatusChip>
            {{ c.assistant }} — {{ c.groups.join(", ") }} ({{
              c.confirmed_at.slice(0, 10)
            }})
          </li>
        </ul>
      </section>
    </template>

    <SidePanel
      v-model="readerOpen"
      :title="agreement?.meta.id || 'Agreement'"
      :subtitle="agreement?.label ?? ''"
    >
      <AgreementViewer v-if="agreement" :agreement="agreement" />
    </SidePanel>
  </div>
</template>

<style scoped>
.mine {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  flex: 1;
  max-width: 60rem;
}
.mine__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
}
.mine__head h1 {
  margin: 0;
  font-size: 1.3rem;
}
.mine__head p {
  margin: 0.2rem 0 0;
}
.block h2 {
  margin: 0.5rem 0 0.2rem;
  font-size: 1rem;
}
.block > p {
  margin: 0 0 0.5rem;
}
.item {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.7rem 0.9rem;
  margin-top: 0.4rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.item__title {
  font-weight: 600;
}
.item__main {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.item__actions {
  display: flex;
  gap: 0.4rem;
  flex: none;
}
.decisions {
  margin: 0.2rem 0 0;
  color: var(--color-muted);
}
.decisions summary {
  cursor: pointer;
}
.decisions ul {
  margin: 0.3rem 0 0;
  padding-left: 1.1rem;
}
.done ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.clear {
  margin: 0;
  font-size: 1.02rem;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.84rem;
}
.gap-text {
  color: var(--color-error);
}
.warn-text {
  color: var(--color-warn-ink);
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
  font: inherit;
}
@media (max-width: 640px) {
  .item {
    flex-direction: column;
  }
}
.round {
  margin: 0;
  padding: 0.6rem 0.8rem;
  border-radius: var(--radius-md);
  background: color-mix(in srgb, var(--color-accent) 10%, transparent);
  font-size: 0.9rem;
}
.item--col {
  flex-direction: column;
  align-items: stretch;
  gap: 0.6rem;
}
.item__row {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-start;
}
.checks summary {
  cursor: pointer;
  color: var(--color-muted);
}
.checks table {
  border-collapse: collapse;
  margin-top: 0.3rem;
}
.checks td {
  padding: 0.15rem 0.6rem 0.15rem 0;
  vertical-align: top;
}
.v {
  font-weight: 700;
}
.v--pass {
  color: var(--color-success);
}
.v--fail {
  color: var(--color-error);
}
.v--inconclusive {
  color: var(--color-warn-ink);
}
.check-form {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.4rem;
  max-width: 30rem;
}
.check-form input {
  font: inherit;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
}
.check-form .btn {
  align-self: flex-start;
}
@media (max-width: 640px) {
  .item__row {
    flex-direction: column;
  }
}
.note {
  margin-top: 0.35rem;
  width: 100%;
  max-width: 28rem;
  font: inherit;
  font-size: 0.85rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
}
</style>
